import { mnemonicToPrivateKey } from '@ton/crypto'
import { enrollLimitSigner, fetchWalletSigningMaterial } from '../api/userland'
import type { UserlandWallet } from '../api/userland'
import type { AccountSecretEnvelope } from './clientVault'
import { loadDeviceWalletSeed, persistDeviceWalletSeed } from './deviceWalletSeed'
import { addressToRawForm } from './tonRawAddress'
import { deriveTonV5R1Address } from './tonWalletV5'

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function sameWallet(a: string, b: string): boolean {
  const left = addressToRawForm(a)
  const right = addressToRawForm(b)
  if (left && right) return left === right
  return a === b
}

export type LimitSignerVault = {
  canSign?: boolean
  status?: string
  reload: () => Promise<string>
  decryptSeed: (envelope: AccountSecretEnvelope) => Promise<string>
}

/** Uploads the 32-byte v5r1 signing seed of THIS wallet so the server can
 *  fill limits from the same balance as Instant. The 24-word phrase never
 *  leaves the tab. Same seed source as Instant: device copy, then vault. */
export async function enrollPrimaryLimitSigner(
  wallet: UserlandWallet,
  vault: LimitSignerVault
): Promise<void> {
  const words = await loadSeedWords(wallet, vault)
  const keyPair = await mnemonicToPrivateKey(words.trim().split(/\s+/))
  const seed = new Uint8Array(keyPair.secretKey.subarray(0, 32))
  try {
    const derived = deriveTonV5R1Address({ publicKey: new Uint8Array(keyPair.publicKey) })
    if (!sameWallet(derived.friendly, wallet.address) && !sameWallet(derived.raw, wallet.address)) {
      throw new Error('seed does not belong to this wallet')
    }
    await persistDeviceWalletSeed(wallet.address, words)
    await enrollLimitSigner({ wallet: wallet.address, signing_seed_hex: bytesToHex(seed) })
  } finally {
    seed.fill(0)
    keyPair.secretKey.fill(0)
  }
}

async function loadSeedWords(wallet: UserlandWallet, vault: LimitSignerVault): Promise<string> {
  const local = await loadDeviceWalletSeed(wallet.address)
  if (local) return local
  if (!(await vaultCanSign(vault))) {
    throw new Error('Wallet seed is not on this device')
  }
  const exported = await fetchWalletSigningMaterial(wallet.id)
  if (exported.kind !== 'account-ecies-v1' && exported.kind !== 'account-dek-v1') {
    throw new Error('this wallet must be re-imported into secure account storage to trade')
  }
  return vault.decryptSeed(exported.seed)
}

async function vaultCanSign(vault: LimitSignerVault): Promise<boolean> {
  if (vault.canSign) return true
  if (vault.canSign === undefined && vault.status !== 'locked' && vault.status !== 'none') {
    if (vault.status === 'unlocked' || vault.status === undefined) return true
  }
  const status = await vault.reload()
  return status === 'unlocked' || Boolean(vault.canSign)
}
